import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { BellRing, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 15000;
const ALARM_MAX_MS = 25000;

// Web Audio alarm (no asset needed). Beeps until stopped or timeout.
let audioCtx = null;
function beep() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    audioCtx = audioCtx || new Ctx();
    if (audioCtx.state === 'suspended') audioCtx.resume();
    const t = audioCtx.currentTime;
    [880, 1175].forEach((freq, i) => {
      const o = audioCtx.createOscillator();
      const g = audioCtx.createGain();
      o.connect(g); g.connect(audioCtx.destination);
      o.type = 'sine';
      o.frequency.value = freq;
      const start = t + i * 0.18;
      g.gain.setValueAtTime(0.0001, start);
      g.gain.exponentialRampToValueAtTime(0.35, start + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, start + 0.16);
      o.start(start);
      o.stop(start + 0.18);
    });
  } catch { /* ignore */ }
}

const NewOrderWatcher = () => {
  const navigate = useNavigate();
  const { vendor } = useAuth();
  const [popup, setPopup] = useState(null); // { order, extra }
  const [currency, setCurrency] = useState('₹');

  const lastSeenRef = useRef(null);
  const initRef = useRef(false);
  const alarmIntervalRef = useRef(null);
  const alarmTimeoutRef = useRef(null);

  const storageKey = vendor?.id ? `saanjh_last_order_${vendor.id}` : 'saanjh_last_order';

  const stopAlarm = useCallback(() => {
    if (alarmIntervalRef.current) clearInterval(alarmIntervalRef.current);
    if (alarmTimeoutRef.current) clearTimeout(alarmTimeoutRef.current);
    alarmIntervalRef.current = null;
    alarmTimeoutRef.current = null;
  }, []);

  const startAlarm = useCallback(() => {
    stopAlarm();
    beep();
    alarmIntervalRef.current = setInterval(beep, 1100);
    alarmTimeoutRef.current = setTimeout(stopAlarm, ALARM_MAX_MS);
  }, [stopAlarm]);

  // currency symbol for the popup
  useEffect(() => {
    const token = localStorage.getItem('vendor_token');
    if (!token) return;
    axios.get(`${API}/vendor/theme`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setCurrency(r.data?.theme?.currency_symbol || '₹'))
      .catch(() => {});
  }, []);

  useEffect(() => {
    let active = true;
    const poll = async () => {
      const token = localStorage.getItem('vendor_token');
      if (!token) return;
      try {
        const { data } = await axios.get(`${API}/orders`, { headers: { Authorization: `Bearer ${token}` } });
        const orders = (data.orders || []).slice().sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
        if (!active || orders.length === 0) {
          if (!initRef.current) initRef.current = true;
          return;
        }
        const newest = orders[0];

        if (!initRef.current) {
          // First run after load: set the baseline, don't alarm for old orders.
          lastSeenRef.current = localStorage.getItem(storageKey) || newest.created_at;
          // Bump baseline forward so existing orders never trigger an alarm.
          if ((newest.created_at || '') > (lastSeenRef.current || '')) {
            lastSeenRef.current = newest.created_at;
          }
          localStorage.setItem(storageKey, lastSeenRef.current);
          initRef.current = true;
          return;
        }

        const lastSeen = lastSeenRef.current || localStorage.getItem(storageKey) || '';
        const fresh = orders.filter((o) => (o.created_at || '') > lastSeen);
        if (fresh.length > 0) {
          lastSeenRef.current = newest.created_at;
          localStorage.setItem(storageKey, newest.created_at);
          setPopup({ order: fresh[0], extra: fresh.length - 1 });
          startAlarm();
        }
      } catch { /* ignore transient errors */ }
    };

    poll();
    const id = setInterval(poll, POLL_MS);
    return () => { active = false; clearInterval(id); stopAlarm(); };
  }, [storageKey, startAlarm, stopAlarm]);

  const handleView = () => {
    const order = popup?.order;
    stopAlarm();
    setPopup(null);
    if (order) navigate(`/dashboard/orders?focus=${order.id}`);
  };

  const handleDismiss = () => {
    stopAlarm();
    setPopup(null);
  };

  if (!popup) return null;
  const { order, extra } = popup;
  const itemCount = (order.items || []).reduce((s, i) => s + (i.quantity || 0), 0);

  return (
    <div className="fixed inset-0 z-[100] flex items-start sm:items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="w-full max-w-sm rounded-2xl bg-white shadow-2xl overflow-hidden mt-10 sm:mt-0 animate-scale">
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-5 py-4 flex items-center gap-3 text-white">
          <span className="relative flex h-11 w-11 items-center justify-center rounded-full bg-white/20">
            <BellRing className="h-6 w-6 animate-bounce" />
          </span>
          <div className="flex-1">
            <p className="font-bold text-lg leading-tight">New order!</p>
            <p className="text-white/90 text-sm">Tap to confirm or close it</p>
          </div>
          <button onClick={handleDismiss} aria-label="Dismiss" className="text-white/80 hover:text-white">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <div className="flex justify-between gap-3">
            <span className="text-muted-foreground text-sm">Customer</span>
            <span className="font-semibold text-sm text-right">{order.customer_name}</span>
          </div>
          <div className="flex justify-between gap-3">
            <span className="text-muted-foreground text-sm">Items</span>
            <span className="font-semibold text-sm">{itemCount}</span>
          </div>
          <div className="flex justify-between gap-3">
            <span className="text-muted-foreground text-sm">Total</span>
            <span className="font-bold text-lg">{currency}{Number(order.total_amount || 0).toFixed(2)}</span>
          </div>
          <div className="flex justify-between gap-3 items-center">
            <span className="text-muted-foreground text-sm">Requested for</span>
            <span className={`text-sm font-semibold rounded-full px-2.5 py-0.5 ${order.fulfilment === 'scheduled' ? 'bg-indigo-100 text-indigo-800' : 'bg-emerald-100 text-emerald-800'}`}>
              🕒 {order.fulfilment === 'scheduled' ? (order.scheduled_label || order.scheduled_for || 'Scheduled') : 'ASAP'}
            </span>
          </div>
          {extra > 0 && (
            <p className="text-xs text-purple-700 bg-purple-50 rounded-md px-3 py-2 text-center">
              +{extra} more new order{extra > 1 ? 's' : ''} waiting
            </p>
          )}
          <div className="pt-1 flex gap-2">
            <Button variant="outline" className="flex-1" onClick={handleDismiss} data-testid="order-alert-dismiss">
              Dismiss
            </Button>
            <Button
              className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
              onClick={handleView}
              data-testid="order-alert-view"
            >
              View order
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewOrderWatcher;
